import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  AMNIOTIC_FLUID_LABELS,
  AMNIOTIC_FLUID_STATES,
  DELIVERY_OUTCOME_LABELS,
  DELIVERY_OUTCOMES,
  DELIVERY_MODE_LABELS,
  DELIVERY_MODES,
  FEEDING_LABELS,
  FEEDING_STATES,
  formatGestation,
  LABOUR_STATUS_LABELS,
  MOULDING_GRADES,
  MOULDING_LABELS,
  PLACENTA_LABELS,
  PLACENTA_STATES,
  YES_NO_NOT_RECORDED,
  YES_NO_NOT_RECORDED_LABELS,
  type AmnioticFluid,
  type DeliveryOutcome,
  type DeliveryMode,
  type FeedingState,
  type Moulding,
  type PlacentaState,
  type YesNoNotRecorded,
} from '@wodi/domain';
import type { LabourObservationView, LabourRecordView, PostnatalCheckView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Kbd,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import {
  deliveryInError,
  labourObservationInError,
  labourQuery,
  maternityKeys,
  postnatalCheckInError,
  recordDelivery,
  recordLabourObservation,
  recordPostnatalCheck,
} from '../api/maternity.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { toasts } from '../toasts.ts';
import { BabyLine } from './baby-line.tsx';
import { PartographChart } from './charts.tsx';
import { LabourActions, labourEndWords } from './labour-actions.tsx';
import {
  DangerSignChecklist,
  DangerSignWords,
  embeddedPayload,
  EmbeddedVitalsFields,
  errorText,
  InErrorDialog,
  MATERNAL_VITALS,
  noteServerAlerts,
  readDecimal,
  readWhole,
  saveErrorText,
  setValues,
  useEmbeddedVitals,
  useVitalAlerts,
  useWhen,
  WhenFields,
} from './common.tsx';
import styles from './maternity.module.css';

// The labour record (docs/plans/phase2-maternity-mch.md §1.1 items 3–8, §6): timed
// observations and the partograph built from them, the delivery as entered, the babies with
// their birth-notification state (plan §5, baby-line.tsx), and postnatal checks. Nothing here
// judges a value against a threshold; the vitals go through the triage rules.

const route = getRouteApi('/_app/labour/$labourRecordId');
const NOT_SAID = 'not_said';

function useSetView(id: string) {
  const queryClient = useQueryClient();
  return (next: LabourRecordView) => {
    queryClient.setQueryData(maternityKeys.labour(id), next);
    void queryClient.invalidateQueries({ queryKey: maternityKeys.labourWard });
    void queryClient.invalidateQueries({ queryKey: maternityKeys.patient(next.patientId) });
    void queryClient.invalidateQueries({ queryKey: ['patients'] });
  };
}

// --- Observations -------------------------------------------------------------------------------

function ObservationForm({ view }: { view: LabourRecordView }) {
  const online = useOnline();
  const setView = useSetView(view.labour.id);
  const vitals = useEmbeddedVitals(view.patientAgeDays);
  const alerts = useVitalAlerts(vitals, MATERNAL_VITALS);
  const when = useWhen();
  const [dilation, setDilation] = useState('');
  const [descent, setDescent] = useState('');
  const [contractions, setContractions] = useState('');
  const [seconds, setSeconds] = useState('');
  const [fhr, setFhr] = useState('');
  const [fluid, setFluid] = useState<string>(NOT_SAID);
  const [moulding, setMoulding] = useState<string>(NOT_SAID);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const requestId = useRef(crypto.randomUUID());
  const values = {
    dilation: readDecimal(dilation),
    descent: readWhole(descent),
    contractions: readWhole(contractions),
    seconds: readWhole(seconds),
    fhr: readWhole(fhr),
  };
  const payload = embeddedPayload(vitals, alerts);
  const invalid =
    Object.values(values).some((v) => v === 'invalid') || when.invalid || !payload.ready;
  const n = (v: number | null | 'invalid') => (v === 'invalid' ? null : v);
  const save = useMutation({
    mutationFn: () =>
      recordLabourObservation(view.labour.id, {
        clientRequestId: requestId.current,
        ...(when.value === undefined ? {} : { observedAt: when.value }),
        cervicalDilationCm: n(values.dilation),
        descentFifths: n(values.descent),
        contractionsPer10Min: n(values.contractions),
        contractionSeconds: n(values.seconds),
        fetalHeartRate: n(values.fhr),
        amnioticFluid: fluid === NOT_SAID ? null : (fluid as AmnioticFluid),
        moulding: moulding === NOT_SAID ? null : (moulding as Moulding),
        note: note.trim() === '' ? null : note.trim(),
        vitals: payload.vitals,
      }),
    onSuccess: (next) => {
      setView(next);
      requestId.current = crypto.randomUUID();
      for (const reset of [setDilation, setDescent, setContractions, setSeconds, setFhr, setNote])
        reset('');
      setFluid(NOT_SAID);
      setMoulding(NOT_SAID);
      vitals.setTexts({});
      vitals.setReasons({});
      vitals.setServerAlerts([]);
      setTried(false);
      toasts.show({ title: 'Observation saved' });
    },
    onError: (error) => {
      noteServerAlerts(error, vitals);
      requestId.current = crypto.randomUUID();
    },
  });
  const submit = () => {
    setTried(true);
    if (!online || invalid || save.isPending) return;
    save.mutate();
  };
  useShortcut('mod+enter', submit, { allowInFields: true, description: 'Save observation' });
  const bad = (v: number | null | 'invalid', words: string) =>
    v === 'invalid' ? { errorMessage: words } : {};
  const saveError = saveErrorText(save.error, 'The observation was not saved. Try again.');
  return (
    <form
      className={styles.form}
      aria-label="Record labour observation"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <WhenFields label="Observed" when={when} />
      <div className={styles.grid}>
        <TextField
          label="Cervical dilatation"
          unit="cm"
          inputMode="decimal"
          value={dilation}
          onChange={setDilation}
          {...bad(values.dilation, 'Centimetres, for example 4 or 4.5.')}
        />
        <TextField
          label="Descent"
          unit="fifths"
          inputMode="numeric"
          value={descent}
          onChange={setDescent}
          {...bad(values.descent, 'Fifths palpable, 0 to 5.')}
        />
        <TextField
          label="Contractions"
          unit="in 10 min"
          inputMode="numeric"
          value={contractions}
          onChange={setContractions}
          {...bad(values.contractions, 'A whole number.')}
        />
        <TextField
          label="Contraction length"
          unit="s"
          inputMode="numeric"
          value={seconds}
          onChange={setSeconds}
          {...bad(values.seconds, 'Whole seconds.')}
        />
        <TextField
          label="Fetal heart rate"
          unit="/min"
          inputMode="numeric"
          value={fhr}
          onChange={setFhr}
          {...bad(values.fhr, 'A whole number.')}
        />
      </div>
      <div className={styles.when}>
        <Select
          label="Amniotic fluid"
          width="sm"
          options={[
            { id: NOT_SAID, label: 'Not recorded' },
            ...AMNIOTIC_FLUID_STATES.map((s) => ({ id: s, label: AMNIOTIC_FLUID_LABELS[s] })),
          ]}
          value={fluid}
          onChange={(v) => {
            setFluid(String(v));
          }}
        />
        <Select
          label="Moulding"
          width="sm"
          options={[
            { id: NOT_SAID, label: 'Not recorded' },
            ...MOULDING_GRADES.map((s) => ({ id: s, label: MOULDING_LABELS[s] })),
          ]}
          value={moulding}
          onChange={(v) => {
            setMoulding(String(v));
          }}
        />
      </div>
      <EmbeddedVitalsFields state={vitals} keys={MATERNAL_VITALS} tried={tried} />
      <TextArea label="Note" value={note} onChange={setNote} rows={2} />
      {saveError === null ? null : <Notice tone="error">{saveError}</Notice>}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save observation
        </Button>
        <Kbd combo="mod+enter" />
      </div>
    </form>
  );
}

function observationWords(o: LabourObservationView): string {
  const parts = [
    o.cervicalDilationCm === null ? null : `Dilatation ${String(o.cervicalDilationCm)} cm`,
    o.descentFifths === null ? null : `Descent ${String(o.descentFifths)}/5`,
    o.contractionsPer10Min === null
      ? null
      : `Contractions ${String(o.contractionsPer10Min)} in 10 min${o.contractionSeconds === null ? '' : `, ${String(o.contractionSeconds)} s`}`,
    o.fetalHeartRate === null ? null : `FHR ${String(o.fetalHeartRate)}/min`,
    o.amnioticFluid === null
      ? null
      : `Fluid: ${AMNIOTIC_FLUID_LABELS[o.amnioticFluid].toLowerCase()}`,
    o.moulding === null ? null : `Moulding ${MOULDING_LABELS[o.moulding]}`,
    o.vitalSet === null ? null : setValues(o.vitalSet),
    o.note,
  ];
  return parts.filter((p) => p !== null).join('; ');
}

function Observations({ view, mayRecord }: { view: LabourRecordView; mayRecord: boolean }) {
  const setView = useSetView(view.labour.id);
  const [marking, setMarking] = useState<LabourObservationView | null>(null);
  const mark = useMutation({
    mutationFn: (reason: string) => labourObservationInError(marking?.id ?? '', reason),
    onSuccess: (next) => {
      setView(next);
      toasts.show({ title: 'Observation entered in error' });
      setMarking(null);
    },
  });
  return (
    <>
      <Table flat dense aria-label="Labour observations">
        <TableHeader>
          <Column isRowHeader>Observed</Column>
          <Column>Values</Column>
          <Column>Recorded by</Column>
          <Column>State</Column>
        </TableHeader>
        <TableBody items={view.observations} renderEmptyState={() => 'No observations yet.'}>
          {(o) => (
            <Row id={o.id}>
              <Cell>
                <span className={styles.numeric}>{formatInstant(o.observedAt)}</span>
              </Cell>
              <Cell>{observationWords(o)}</Cell>
              <Cell>{o.recordedBy.displayName}</Cell>
              <Cell>
                {o.status === 'entered_in_error' ? (
                  <Tag state="neutral">Entered in error</Tag>
                ) : mayRecord ? (
                  <Button
                    variant="quiet"
                    dense
                    onPress={() => {
                      setMarking(o);
                    }}
                  >
                    Enter in error
                  </Button>
                ) : (
                  <Tag state="done">Recorded</Tag>
                )}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {marking === null ? null : (
        <InErrorDialog
          what="this observation"
          onClose={() => {
            setMarking(null);
          }}
          onConfirm={(reason) => {
            mark.mutate(reason);
          }}
          pending={mark.isPending}
          error={mark.error}
        />
      )}
    </>
  );
}

// --- Delivery -----------------------------------------------------------------------------------

interface BabyDraft {
  outcome: DeliveryOutcome;
  sex: string;
  weight: string;
  apgar1: string;
  apgar5: string;
  note: string;
}

const NEW_BABY: BabyDraft = {
  outcome: 'live_birth',
  sex: NOT_SAID,
  weight: '',
  apgar1: '',
  apgar5: '',
  note: '',
};

function DeliveryForm({ view }: { view: LabourRecordView }) {
  const online = useOnline();
  const setView = useSetView(view.labour.id);
  const { data: session } = useSuspenseQuery(sessionQuery);
  const when = useWhen();
  const placentaWhen = useWhen();
  const [placentaRecorded, setPlacentaRecorded] = useState(false);
  const [mode, setMode] = useState<DeliveryMode>('spontaneous_vaginal');
  const [caseId, setCaseId] = useState(view.theatreCases[0]?.id ?? '');
  const ownId = session?.user.id ?? '';
  const [attendant, setAttendant] = useState(
    view.attendants.some((a) => a.id === ownId) ? ownId : (view.attendants[0]?.id ?? ''),
  );
  const [placenta, setPlacenta] = useState<PlacentaState>('not_recorded');
  const [uterotonic, setUterotonic] = useState<YesNoNotRecorded>('not_recorded');
  const [bloodLoss, setBloodLoss] = useState('');
  const [perineum, setPerineum] = useState('');
  const [complications, setComplications] = useState('');
  const [babies, setBabies] = useState<BabyDraft[]>([NEW_BABY]);
  const requestId = useRef(crypto.randomUUID());
  const caesarean = mode === 'caesarean_section';
  const loss = readWhole(bloodLoss);
  const babyNumbers = babies.map((b) => ({
    weight: readWhole(b.weight),
    apgar1: readWhole(b.apgar1),
    apgar5: readWhole(b.apgar5),
  }));
  const deliveredAt = when.instant;
  const invalid =
    deliveredAt === null ||
    loss === 'invalid' ||
    (placentaRecorded && placentaWhen.instant === null) ||
    babyNumbers.some((b) => Object.values(b).some((v) => v === 'invalid')) ||
    (caesarean && caseId === '') ||
    attendant === '';
  const n = (v: number | null | 'invalid') => (v === 'invalid' ? null : v);
  const save = useMutation({
    mutationFn: () => {
      const at = (deliveredAt ?? new Date()).toISOString();
      return recordDelivery(view.labour.id, {
        clientRequestId: requestId.current,
        deliveredAt: at,
        mode,
        theatreCaseId: caesarean ? caseId : null,
        attendantUserId: attendant,
        placentaDeliveredAt:
          placentaRecorded && placentaWhen.instant !== null
            ? placentaWhen.instant.toISOString()
            : null,
        placentaComplete: placenta,
        uterotonicGiven: uterotonic,
        bloodLossMl: n(loss),
        perineum: perineum.trim() === '' ? null : perineum.trim(),
        complications: complications.trim() === '' ? null : complications.trim(),
        babies: babies.map((b, i) => {
          const sex = view.sexOptions.find((s) => s.code === b.sex);
          const numbers = babyNumbers[i];
          const live = b.outcome === 'live_birth';
          return {
            birthOrder: i + 1,
            bornAt: at,
            outcome: b.outcome,
            sex: sex === undefined ? null : { system: sex.system, code: sex.code },
            birthWeightGrams: n(numbers?.weight ?? null),
            apgar1: live ? n(numbers?.apgar1 ?? null) : null,
            apgar5: live ? n(numbers?.apgar5 ?? null) : null,
            note: b.note.trim() === '' ? null : b.note.trim(),
          };
        }),
      });
    },
    onSuccess: (next) => {
      setView(next);
      toasts.show({ title: 'Delivery recorded' });
    },
    onError: () => {
      requestId.current = crypto.randomUUID();
    },
  });
  const update = (i: number, patch: Partial<BabyDraft>) => {
    setBabies(babies.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  };
  const whole = (v: number | null | 'invalid') =>
    v === 'invalid' ? { errorMessage: 'A whole number.' } : {};
  return (
    <form
      className={styles.form}
      aria-label="Record delivery"
      onSubmit={(event) => {
        event.preventDefault();
        if (online && !invalid) save.mutate();
      }}
    >
      <WhenFields label="Delivered" when={when} />
      <RadioGroup
        label="Mode of delivery"
        value={mode}
        onChange={(v) => {
          setMode(v as DeliveryMode);
        }}
        options={DELIVERY_MODES.map((m) => ({ id: m, label: DELIVERY_MODE_LABELS[m] }))}
      />
      {caesarean ? (
        view.theatreCases.length === 0 ? (
          <Notice tone="info">
            A caesarean section is linked to its theatre case. Book the case on this stay first.
          </Notice>
        ) : (
          <Select
            label="Theatre case"
            width="md"
            options={view.theatreCases.map((c) => ({
              id: c.id,
              label: `${c.procedureName}, ${formatInstant(c.scheduledStart)}`,
            }))}
            value={caseId}
            onChange={(v) => {
              setCaseId(String(v));
            }}
          />
        )
      ) : null}
      <Select
        label="Attendant"
        width="md"
        options={view.attendants.map((a) => ({ id: a.id, label: a.displayName }))}
        value={attendant}
        onChange={(v) => {
          setAttendant(String(v));
        }}
      />
      {babies.map((b, i) => (
        <fieldset key={i} className={`${styles.fieldset} ${styles.baby}`}>
          <legend className={styles.legend}>{`Baby ${String(i + 1)}`}</legend>
          <RadioGroup
            label="Outcome"
            orientation="horizontal"
            value={b.outcome}
            onChange={(v) => {
              update(i, { outcome: v as DeliveryOutcome });
            }}
            options={DELIVERY_OUTCOMES.map((o) => ({ id: o, label: DELIVERY_OUTCOME_LABELS[o] }))}
          />
          <div className={styles.grid}>
            <Select
              label="Sex"
              options={[
                { id: NOT_SAID, label: 'Not recorded' },
                ...view.sexOptions.map((s) => ({ id: s.code, label: s.display })),
              ]}
              value={b.sex}
              onChange={(v) => {
                update(i, { sex: String(v) });
              }}
            />
            <TextField
              label="Birth weight"
              unit="g"
              inputMode="numeric"
              value={b.weight}
              onChange={(weight) => {
                update(i, { weight });
              }}
              {...whole(babyNumbers[i]?.weight ?? null)}
            />
            {b.outcome === 'live_birth' ? (
              <>
                <TextField
                  label="Apgar at 1 minute"
                  inputMode="numeric"
                  value={b.apgar1}
                  onChange={(apgar1) => {
                    update(i, { apgar1 });
                  }}
                  {...whole(babyNumbers[i]?.apgar1 ?? null)}
                />
                <TextField
                  label="Apgar at 5 minutes"
                  inputMode="numeric"
                  value={b.apgar5}
                  onChange={(apgar5) => {
                    update(i, { apgar5 });
                  }}
                  {...whole(babyNumbers[i]?.apgar5 ?? null)}
                />
              </>
            ) : null}
          </div>
          <TextField
            label="Note about this baby"
            value={b.note}
            onChange={(note) => {
              update(i, { note });
            }}
          />
          {babies.length > 1 ? (
            <Button
              variant="quiet"
              dense
              onPress={() => {
                setBabies(babies.filter((_, j) => j !== i));
              }}
            >
              {`Remove baby ${String(i + 1)}`}
            </Button>
          ) : null}
        </fieldset>
      ))}
      <div className={styles.actions}>
        <Button
          isDisabled={babies.length >= 9}
          onPress={() => {
            setBabies([...babies, NEW_BABY]);
          }}
        >
          Add another baby
        </Button>
      </div>
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Third stage</legend>
        <RadioGroup
          label="Placenta delivered at a recorded time"
          orientation="horizontal"
          value={placentaRecorded ? 'yes' : 'no'}
          onChange={(v) => {
            setPlacentaRecorded(v === 'yes');
          }}
          options={[
            { id: 'no', label: 'Not recorded' },
            { id: 'yes', label: 'Record the time' },
          ]}
        />
        {placentaRecorded ? <WhenFields label="Placenta" when={placentaWhen} /> : null}
        <RadioGroup
          label="Placenta complete"
          orientation="horizontal"
          value={placenta}
          onChange={(v) => {
            setPlacenta(v as PlacentaState);
          }}
          options={PLACENTA_STATES.map((s) => ({ id: s, label: PLACENTA_LABELS[s] }))}
        />
        <RadioGroup
          label="Uterotonic given"
          description="Any medicine and dose is on the medicines chart, not here."
          orientation="horizontal"
          value={uterotonic}
          onChange={(v) => {
            setUterotonic(v as YesNoNotRecorded);
          }}
          options={YES_NO_NOT_RECORDED.map((s) => ({
            id: s,
            label: YES_NO_NOT_RECORDED_LABELS[s],
          }))}
        />
        <div className={styles.grid}>
          <TextField
            label="Blood loss"
            unit="ml"
            inputMode="numeric"
            value={bloodLoss}
            onChange={setBloodLoss}
            description="As estimated."
            {...whole(loss)}
          />
          <TextField label="Perineum" value={perineum} onChange={setPerineum} />
        </div>
      </fieldset>
      <TextArea label="Complications" value={complications} onChange={setComplications} rows={2} />
      {save.error === null ? null : (
        <Notice tone="error">
          {errorText(save.error, 'The delivery was not recorded. Try again.')}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || invalid}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Record delivery
        </Button>
      </div>
    </form>
  );
}

function DeliverySummary({ view, mayRecord }: { view: LabourRecordView; mayRecord: boolean }) {
  const setView = useSetView(view.labour.id);
  const [marking, setMarking] = useState(false);
  const d = view.delivery;
  const mark = useMutation({
    mutationFn: (reason: string) => deliveryInError(d?.id ?? '', reason),
    onSuccess: (next) => {
      setView(next);
      toasts.show({ title: 'Delivery entered in error' });
      setMarking(false);
    },
  });
  if (d === null) return null;
  return (
    <Section
      title="Delivery"
      actions={
        d.status === 'entered_in_error' ? (
          <Tag state="neutral">Entered in error</Tag>
        ) : mayRecord ? (
          <Button
            variant="quiet"
            dense
            onPress={() => {
              setMarking(true);
            }}
          >
            Enter in error
          </Button>
        ) : undefined
      }
    >
      <dl className={styles.facts}>
        <dt>Delivered</dt>
        <dd>{formatInstant(d.deliveredAt)}</dd>
        <dt>Mode</dt>
        <dd>
          {DELIVERY_MODE_LABELS[d.mode]}
          {d.theatreCase === null ? null : (
            <>
              {', '}
              <Link to="/theatre/cases/$caseId" params={{ caseId: d.theatreCase.id }}>
                {d.theatreCase.procedureName}
              </Link>
            </>
          )}
        </dd>
        <dt>Attendant</dt>
        <dd>{d.attendant.displayName}</dd>
        <dt>Placenta</dt>
        <dd>{`${PLACENTA_LABELS[d.placentaComplete]}${d.placentaDeliveredAt === null ? '' : `, ${formatInstant(d.placentaDeliveredAt)}`}`}</dd>
        <dt>Uterotonic given</dt>
        <dd>{YES_NO_NOT_RECORDED_LABELS[d.uterotonicGiven]}</dd>
        <dt>Blood loss</dt>
        <dd>
          {d.bloodLossMl === null ? 'Not recorded' : `${String(d.bloodLossMl)} ml, as estimated`}
        </dd>
        {d.perineum === null ? null : (
          <>
            <dt>Perineum</dt>
            <dd>{d.perineum}</dd>
          </>
        )}
        {d.complications === null ? null : (
          <>
            <dt>Complications</dt>
            <dd>{d.complications}</dd>
          </>
        )}
        {d.statusReason === null ? null : (
          <>
            <dt>Reason</dt>
            <dd>{d.statusReason}</dd>
          </>
        )}
      </dl>
      <ul className={styles.form} aria-label="Babies">
        {d.babies.map((b) => (
          <BabyLine
            key={b.id}
            baby={b}
            delivery={d}
            mayRecord={mayRecord && d.status === 'active'}
            labourId={view.labour.id}
          />
        ))}
      </ul>
      {marking ? (
        <InErrorDialog
          what="this delivery"
          onClose={() => {
            setMarking(false);
          }}
          onConfirm={(reason) => {
            mark.mutate(reason);
          }}
          pending={mark.isPending}
          error={mark.error}
        />
      ) : null}
    </Section>
  );
}

// --- Postnatal ----------------------------------------------------------------------------------

function PostnatalForm({ view }: { view: LabourRecordView }) {
  const online = useOnline();
  const setView = useSetView(view.labour.id);
  const delivery = view.delivery;
  const vitals = useEmbeddedVitals(view.patientAgeDays);
  const alerts = useVitalAlerts(vitals, MATERNAL_VITALS);
  const when = useWhen();
  const [subject, setSubject] = useState('mother');
  const [visitId, setVisitId] = useState(view.openVisits[0]?.id ?? '');
  const [feeding, setFeeding] = useState<string>(NOT_SAID);
  const [weight, setWeight] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [none, setNone] = useState(false);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const requestId = useRef(crypto.randomUUID());
  const babyId = subject === 'mother' ? null : subject;
  const payload = embeddedPayload(vitals, alerts);
  const grams = readWhole(weight);
  const invalid =
    visitId === '' || when.invalid || grams === 'invalid' || (babyId === null && !payload.ready);
  const save = useMutation({
    mutationFn: () =>
      recordPostnatalCheck(delivery?.id ?? '', {
        clientRequestId: requestId.current,
        visitId,
        subject: babyId === null ? 'mother' : 'baby',
        deliveryBabyId: babyId,
        ...(when.value === undefined ? {} : { checkedAt: when.value }),
        feeding: feeding === NOT_SAID ? null : (feeding as FeedingState),
        dangerSignCodes: codes,
        noDangerSigns: none,
        babyWeightGrams: babyId === null || grams === 'invalid' ? null : grams,
        note: note.trim() === '' ? null : note.trim(),
        vitals: babyId === null ? payload.vitals : null,
      }),
    onSuccess: (next) => {
      setView(next);
      requestId.current = crypto.randomUUID();
      setFeeding(NOT_SAID);
      setWeight('');
      setCodes([]);
      setNone(false);
      setNote('');
      vitals.setTexts({});
      vitals.setReasons({});
      vitals.setServerAlerts([]);
      setTried(false);
      toasts.show({ title: 'Postnatal check saved' });
    },
    onError: (error) => {
      noteServerAlerts(error, vitals);
      requestId.current = crypto.randomUUID();
    },
  });
  if (delivery === null) return null;
  if (view.openVisits.length === 0) {
    return (
      <p className={styles.meta}>
        A postnatal check is recorded on one of the mother's open visits. Check her in first.
      </p>
    );
  }
  const saveError = saveErrorText(save.error, 'The check was not saved. Try again.');
  return (
    <form
      className={styles.form}
      aria-label="Record postnatal check"
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (online && !invalid) save.mutate();
      }}
    >
      <div className={styles.when}>
        <Select
          label="Check of"
          width="sm"
          options={[
            { id: 'mother', label: 'Mother' },
            ...delivery.babies.map((b) => ({ id: b.id, label: `Baby ${String(b.birthOrder)}` })),
          ]}
          value={subject}
          onChange={(v) => {
            setSubject(String(v));
            setCodes([]);
            setNone(false);
          }}
        />
        <Select
          label="Visit"
          width="md"
          options={view.openVisits.map((v) => ({
            id: v.id,
            label: `${v.visitNumber}, ${formatInstant(v.startedAt)}`,
          }))}
          value={visitId}
          onChange={(v) => {
            setVisitId(String(v));
          }}
        />
      </div>
      <WhenFields label="Checked" when={when} />
      {babyId === null ? (
        <EmbeddedVitalsFields state={vitals} keys={MATERNAL_VITALS} tried={tried} />
      ) : (
        <TextField
          label="Baby's weight"
          unit="g"
          width="sm"
          inputMode="numeric"
          value={weight}
          onChange={setWeight}
          description="Other vitals for the baby are taken on the baby's own visit."
          {...(grams === 'invalid' ? { errorMessage: 'Whole grams.' } : {})}
        />
      )}
      <Select
        label="Feeding"
        width="sm"
        options={[
          { id: NOT_SAID, label: 'Not recorded' },
          ...FEEDING_STATES.map((f) => ({ id: f, label: FEEDING_LABELS[f] })),
        ]}
        value={feeding}
        onChange={(v) => {
          setFeeding(String(v));
        }}
      />
      <DangerSignChecklist
        label={babyId === null ? 'Danger signs: mother' : 'Danger signs: baby'}
        list={babyId === null ? view.postnatalMotherDangerSigns : view.postnatalBabyDangerSigns}
        codes={codes}
        onCodes={setCodes}
        none={none}
        onNone={setNone}
      />
      <TextArea label="Note" value={note} onChange={setNote} rows={2} />
      {saveError === null ? null : <Notice tone="error">{saveError}</Notice>}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
        >
          Save postnatal check
        </Button>
      </div>
    </form>
  );
}

function PostnatalTable({ view, mayRecord }: { view: LabourRecordView; mayRecord: boolean }) {
  const setView = useSetView(view.labour.id);
  const [marking, setMarking] = useState<PostnatalCheckView | null>(null);
  const mark = useMutation({
    mutationFn: (reason: string) => postnatalCheckInError(marking?.id ?? '', reason),
    onSuccess: (next) => {
      setView(next);
      toasts.show({ title: 'Postnatal check entered in error' });
      setMarking(null);
    },
  });
  return (
    <>
      <Table flat dense aria-label="Postnatal checks">
        <TableHeader>
          <Column isRowHeader>Checked</Column>
          <Column>Of</Column>
          <Column>Findings</Column>
          <Column>Danger signs</Column>
          <Column>State</Column>
        </TableHeader>
        <TableBody items={view.postnatalChecks} renderEmptyState={() => 'No postnatal checks yet.'}>
          {(c) => (
            <Row id={c.id}>
              <Cell>
                <span className={styles.numeric}>{formatInstant(c.checkedAt)}</span>
              </Cell>
              <Cell>
                {c.subject === 'mother' ? 'Mother' : `Baby ${String(c.birthOrder ?? '')}`}
              </Cell>
              <Cell>
                {[
                  c.vitalSet === null ? null : setValues(c.vitalSet),
                  c.babyWeightGrams === null ? null : `Weight ${String(c.babyWeightGrams)} g`,
                  c.feeding === null ? null : FEEDING_LABELS[c.feeding],
                  c.note,
                ]
                  .filter((x) => x !== null)
                  .join('; ') || 'None entered'}
              </Cell>
              <Cell>
                <DangerSignWords signs={c.dangerSigns} none={c.noDangerSigns} />
              </Cell>
              <Cell>
                {c.status === 'entered_in_error' ? (
                  <Tag state="neutral">Entered in error</Tag>
                ) : mayRecord ? (
                  <Button
                    variant="quiet"
                    dense
                    onPress={() => {
                      setMarking(c);
                    }}
                  >
                    Enter in error
                  </Button>
                ) : (
                  <Tag state="done">Recorded</Tag>
                )}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {marking === null ? null : (
        <InErrorDialog
          what="this check"
          onClose={() => {
            setMarking(null);
          }}
          onConfirm={(reason) => {
            mark.mutate(reason);
          }}
          pending={mark.isPending}
          error={mark.error}
        />
      )}
    </>
  );
}

// --- The page -----------------------------------------------------------------------------------

export function LabourPage() {
  const { labourRecordId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const labour = useQuery(labourQuery(labourRecordId));
  if (session === null || !can(session, 'maternity.read')) {
    return (
      <Notice tone="info">
        You don't have permission to see maternity records. Ask an administrator if you need it.
      </Notice>
    );
  }
  if (labour.isPending) return <LoadingBlock label="Loading the labour record" rows={8} />;
  if (labour.isError) {
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void labour.refetch()}>Try again</Button>}
      >
        {errorText(labour.error, 'The labour record could not be loaded. Try again.')}
      </Notice>
    );
  }
  const view = labour.data;
  const mayRecord = can(session, 'maternity.record');
  const inLabour = view.labour.status === 'in_labour';
  const delivered = view.delivery !== null && view.delivery.status === 'active';
  const g = view.pregnancy.gestationToday;
  const place =
    view.labour.wardName === null
      ? ''
      : `${view.labour.wardName}${view.labour.bedLabel === null ? '' : `, bed ${view.labour.bedLabel}`}. `;
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={view.patientId} />
      <PageHeader
        title="Labour record"
        description={`${place}Opened ${formatInstant(view.labour.openedAt)}.${g === null ? '' : ` Gestation ${formatGestation(g)}.`}${labourEndWords(view.labour)}`}
        actions={
          <nav className={styles.links} aria-label="Related">
            <Tag state={inLabour ? 'active' : 'done'}>
              {LABOUR_STATUS_LABELS[view.labour.status]}
            </Tag>
            {mayRecord ? <LabourActions view={view} /> : null}
            <Link to="/admissions/$admissionId" params={{ admissionId: view.labour.admissionId }}>
              The stay
            </Link>
            <Link to="/patients/$patientId/maternity" params={{ patientId: view.patientId }}>
              Maternity record
            </Link>
          </nav>
        }
      />
      {online ? null : (
        <Notice tone="info">No connection to Wodi. Saving waits for the connection.</Notice>
      )}
      <Section title="Partograph">
        <PartographChart observations={view.observations} lines={view.partograph} />
      </Section>
      {mayRecord && inLabour ? (
        <Section title="Record observation">
          <ObservationForm view={view} />
        </Section>
      ) : null}
      <Section title="Observations">
        <Observations view={view} mayRecord={mayRecord} />
      </Section>
      {mayRecord && inLabour ? (
        <Section
          title="Record delivery"
          description="Everything as entered. Recording it closes the labour record and the pregnancy."
        >
          <DeliveryForm view={view} />
        </Section>
      ) : null}
      <DeliverySummary view={view} mayRecord={mayRecord} />
      {view.delivery === null ? null : (
        <Section title="Postnatal checks">
          {mayRecord && delivered ? <PostnatalForm view={view} /> : null}
          <PostnatalTable view={view} mayRecord={mayRecord} />
        </Section>
      )}
    </div>
  );
}
